import type { AppState, ISODate, PlainDateTimeStr, SourceItem, Timing } from './model.ts';
import { Temporal, addDays, compareDates, daysBetween, hhmmToMinutes, timingInstants } from './time.ts';
import { expandOccurrences } from './recurrence.ts';
import { createLinkedWork, NotFoundError, ValidationError, type OpResult } from './ops.ts';

/**
 * "Hitta tid att förbereda" – lokal, deterministisk sökning efter luckor före en importerad
 * engångsdeadline. Ingen extern tjänst. Bygger BARA på data som finns i AutoDay på den här enheten; en
 * importerad ögonblicksbild kan vara äldre än den externa kalendern.
 *
 * Vad som räknas som upptaget (dokumenterat i docs/DEADLINES-PREP.md):
 *  - Alla egna aktiviteter, även i dolda kalendrar (dold betyder inte ledig) och även klarmarkerade.
 *    Återkomster expanderas med samma motor som kalendern; borttagna förekomster är lediga.
 *  - Importerade poster utom de som källan markerat som lediga (TRANSP:TRANSPARENT, t.ex. SkatteFix-datum).
 *    Poster som saknades i senaste importen räknas som upptagna tills de granskats (försiktigt).
 *  - Heldagar: en heldag som inte är markerad som ledig blockerar hela dagen. Egna heldagsaktiviteter har
 *    ingen ledig/upptagen-inställning och kan betyda resa eller ledighet – de blockerar därför hela dagen.
 *  - En importerad återkomst som motorn inte kan tolka helt (RRULE-delar som inte stöds, RDATE) blockerar
 *    sin tid varje dag i sökfönstret (heldag: alla dagar). Aldrig ett obestyrkt ledighetsbesked.
 * Sökfönstret är begränsat (högst MAX_LOOKAHEAD_DAYS dagar före deadline) och börjar tidigast nu.
 */

export const MAX_LOOKAHEAD_DAYS = 21;
const STEP_MINUTES = 15;

export interface PrepOptions {
  durationMinutes: number;
  /** Daglig sökperiod i lokal tid, HH:mm. */
  windowStart: string;
  windowEnd: string;
  days: 'weekdays' | 'all';
  /** Antal dagar före deadline att söka i (1–21). Standard 14. */
  lookaheadDays?: number;
  maxSlots?: number;
}

export interface BusyRef {
  title: string;
  /** Lokal väggtid i visningszonen, HH:mm (null = heldag). */
  start: string | null;
  end: string | null;
}

export interface PrepSlot {
  date: ISODate;
  start: PlainDateTimeStr;
  end: PlainDateTimeStr;
  timeZone: string;
  /** Dagar mellan förberedelsen och deadline. */
  daysBefore: number;
  /** Närmast före och efter luckan samma dag (för förklaringen). */
  before: BusyRef | null;
  after: BusyRef | null;
  /** Antal upptagna poster samma dag inom sökperioden. */
  busySameDay: number;
}

export type PrepResult =
  | {
      status: 'ok' | 'none';
      slots: PrepSlot[];
      deadlineDate: ISODate;
      searchedFrom: ISODate;
      searchedTo: ISODate;
      /** Antal poster som räknades som upptagna i fönstret, och hur många som räknades försiktigt. */
      busyCount: number;
      uncertain: Array<{ title: string; reason: 'unsupported-recurrence' }>;
      hiddenCalendarsCounted: number;
    }
  | { status: 'unsupported'; reason: 'recurring-source' | 'past' | 'not-found' | 'invalid-options' };

/** Upptagen tid i absoluta millisekunder. Samma definition för enstaka förberedelser och förberedelseplaner. */
export interface Busy {
  start: number;
  end: number;
  title: string;
  allDay: boolean;
  /** Egen aktivitet (event-ID) eller källpost (källnyckel) – för förklaringar och krockkontroll. */
  ref?: string;
}

function validOptions(o: PrepOptions): boolean {
  if (!Number.isInteger(o.durationMinutes) || o.durationMinutes < 15 || o.durationMinutes > 480) return false;
  if (!/^\d{2}:\d{2}$/.test(o.windowStart) || !/^\d{2}:\d{2}$/.test(o.windowEnd)) return false;
  return hhmmToMinutes(o.windowEnd) - hhmmToMinutes(o.windowStart) >= o.durationMinutes;
}

const zdt = (local: string, tz: string) => Temporal.PlainDateTime.from(local).toZonedDateTime(tz, { disambiguation: 'compatible' });
const hm = (ms: number, tz: string) => {
  const z = Temporal.Instant.fromEpochMilliseconds(ms).toZonedDateTimeISO(tz);
  return `${String(z.hour).padStart(2, '0')}:${String(z.minute).padStart(2, '0')}`;
};

/** Källdeadlinens första ögonblick: heldag = dagens början i visningszonen, tidsatt = starttiden. */
export function deadlineStart(src: SourceItem, tz: string): { date: ISODate; ms: number } {
  const t = src.data.timing;
  const { start } = timingInstants(t, tz);
  return { date: t.kind === 'allDay' ? t.start : start.toZonedDateTimeISO(tz).toPlainDate().toString(), ms: start.epochMilliseconds };
}

export function hasUnsupportedRecurrence(s: SourceItem): boolean {
  return s.data.unsupported.some((u) => u.startsWith('RRULE') || u === 'RDATE' || u === 'EXRULE');
}

/** Återkommande källpost (regel, förekomst-override eller otolkbar återkomst) – ingen säker förekomstidentitet. */
export function isRecurringSource(state: AppState, src: SourceItem): boolean {
  return !!(src.data.recurrence || src.recurrenceId || hasUnsupportedRecurrence(src) || Object.values(state.sourceItems).some((c) => c.sourceId === src.sourceId && c.uid === src.uid && c.recurrenceId));
}

export interface BusyResult {
  busy: Busy[];
  uncertain: Array<{ title: string; reason: 'unsupported-recurrence' }>;
  hiddenCalendarsCounted: number;
}

/**
 * Allt som räknas som upptaget i [from, toEx) enligt reglerna ovan. Försiktiga poster redovisas separat.
 * excludeEventIds: egna pass som just nu planeras om och därför inte ska krocka med sig själva –
 * används ENDAST för uttryckligen valda, ändringsbara pass (allt annat ligger kvar i underlaget).
 */
export function busyIntervals(state: AppState, from: ISODate, toEx: ISODate, ignoreKey?: string, excludeEventIds?: ReadonlySet<string>): BusyResult {
  const tz = state.settings.timeZone;
  const busy: Busy[] = [];
  const uncertain: Array<{ title: string; reason: 'unsupported-recurrence' }> = [];
  let hidden = 0;
  const push = (t: Timing, title: string, ref: string) => {
    const { start, end } = timingInstants(t, tz);
    busy.push({ start: start.epochMilliseconds, end: Math.max(end.epochMilliseconds, start.epochMilliseconds + 1), title, allDay: t.kind === 'allDay', ref });
  };
  for (const ev of Object.values(state.events)) {
    if (ev.deleted || excludeEventIds?.has(ev.id)) continue;
    const occ = expandOccurrences(ev, addDays(from, -2), addDays(toEx, 1), tz);
    if (occ.length && state.calendars[ev.calendarId]?.visible === false) hidden++;
    for (const o of occ) {
      if (o.override?.cancelled) continue;
      push(o.timing, o.override?.title ?? ev.title, ev.id);
    }
  }
  for (const s of Object.values(state.sourceItems)) {
    if (s.archived || s.data.transparent || s.key === ignoreKey) continue;
    const occ = expandOccurrences(s.data, addDays(from, -2), addDays(toEx, 1), tz);
    if (occ.length && state.calendars[state.sources[s.sourceId]?.calendarId ?? '']?.visible === false) hidden++;
    if (hasUnsupportedRecurrence(s)) {
      // Okänd återkomst: blockera postens tid VARJE dag i fönstret (heldag = hela dagar).
      uncertain.push({ title: s.data.title || s.data.summary, reason: 'unsupported-recurrence' });
      const t = s.data.timing;
      for (let d = addDays(from, -1); compareDates(d, toEx) < 0; d = addDays(d, 1)) {
        if (t.kind === 'allDay') push({ kind: 'allDay', start: d, endExclusive: addDays(d, 1) }, s.data.title, s.key);
        else {
          const span = daysBetween(t.start.slice(0, 10), t.end.slice(0, 10));
          push({ ...t, start: `${d}${t.start.slice(10)}`, end: `${addDays(d, span)}${t.end.slice(10)}` }, s.data.title, s.key);
        }
      }
      continue;
    }
    for (const o of occ) push(o.timing, s.data.title || s.data.summary, s.key);
  }
  busy.sort((a, b) => a.start - b.start);
  return { busy, uncertain, hiddenCalendarsCounted: hidden };
}

/**
 * Upp till tre förslag (olika dagar, tidigast möjliga lucka per dag) som slutar innan deadline börjar.
 * Återkommande källdeadlines stöds inte här (manuellt flöde) – förberedelsen skulle annars kunna
 * länkas till fel förekomst.
 */
export function findPrepSlots(state: AppState, key: string, opts: PrepOptions, now: Temporal.Instant): PrepResult {
  const src = state.sourceItems[key];
  if (!src || src.archived) return { status: 'unsupported', reason: 'not-found' };
  if (isRecurringSource(state, src)) {
    return { status: 'unsupported', reason: 'recurring-source' };
  }
  if (!validOptions(opts)) return { status: 'unsupported', reason: 'invalid-options' };
  const tz = state.settings.timeZone;
  const dl = deadlineStart(src, tz);
  const nowMs = now.epochMilliseconds;
  if (dl.ms <= nowMs) return { status: 'unsupported', reason: 'past' };
  const today = now.toZonedDateTimeISO(tz).toPlainDate().toString();
  const look = Math.min(MAX_LOOKAHEAD_DAYS, Math.max(1, Math.floor(opts.lookaheadDays ?? 14)));
  const from = compareDates(addDays(dl.date, -look), today) > 0 ? addDays(dl.date, -look) : today;
  const lastDay = dl.date; // dagen för en tidsatt deadline kan ha tid före starten
  const { busy, uncertain, hiddenCalendarsCounted } = busyIntervals(state, from, addDays(lastDay, 1), key);
  const durMs = opts.durationMinutes * 60_000;
  const step = STEP_MINUTES * 60_000;
  const slots: PrepSlot[] = [];
  const maxSlots = Math.max(1, Math.min(3, opts.maxSlots ?? 3));
  let busyInWindow = 0;

  for (let d = from; compareDates(d, lastDay) <= 0 && slots.length < maxSlots; d = addDays(d, 1)) {
    const dow = Temporal.PlainDate.from(d).dayOfWeek;
    if (opts.days === 'weekdays' && dow > 5) continue;
    let winStart = zdt(`${d}T${opts.windowStart}`, tz).epochMilliseconds;
    const winEnd = Math.min(zdt(`${d}T${opts.windowEnd}`, tz).epochMilliseconds, dl.ms);
    // Aldrig i det förflutna: avrunda "nu" uppåt till nästa kvart.
    if (winStart < nowMs) winStart = Math.ceil(nowMs / step) * step;
    if (winEnd - winStart < durMs) continue;
    const dayBusy = busy.filter((b) => b.start < winEnd && b.end > winStart);
    busyInWindow += dayBusy.length;
    // Första lucka: gå framåt förbi varje krock.
    let cand = winStart;
    let guard = 0;
    while (cand + durMs <= winEnd && guard++ < 500) {
      const clash = dayBusy.find((b) => b.start < cand + durMs && b.end > cand);
      if (!clash) break;
      cand = Math.ceil(clash.end / step) * step;
    }
    if (cand + durMs > winEnd) continue;
    const before = [...dayBusy].filter((b) => !b.allDay && b.end <= cand).sort((a, b) => b.end - a.end)[0];
    const after = dayBusy.filter((b) => !b.allDay && b.start >= cand + durMs).sort((a, b) => a.start - b.start)[0];
    const s = Temporal.Instant.fromEpochMilliseconds(cand).toZonedDateTimeISO(tz);
    const e = Temporal.Instant.fromEpochMilliseconds(cand + durMs).toZonedDateTimeISO(tz);
    slots.push({
      date: d,
      start: s.toPlainDateTime().toString({ smallestUnit: 'minute' }),
      end: e.toPlainDateTime().toString({ smallestUnit: 'minute' }),
      timeZone: tz,
      daysBefore: daysBetween(d, dl.date),
      before: before ? { title: before.title, start: hm(before.start, tz), end: hm(before.end, tz) } : null,
      after: after ? { title: after.title, start: hm(after.start, tz), end: hm(after.end, tz) } : null,
      busySameDay: dayBusy.length,
    });
  }
  return { status: slots.length ? 'ok' : 'none', slots, deadlineDate: dl.date, searchedFrom: from, searchedTo: lastDay, busyCount: busyInWindow, uncertain, hiddenCalendarsCounted };
}

/** Är luckan fortfarande ledig (samma regler)? Används direkt före skapandet. */
export function isPrepSlotFree(state: AppState, key: string, slot: Pick<PrepSlot, 'start' | 'end' | 'timeZone'>): boolean {
  const s = zdt(slot.start, slot.timeZone).epochMilliseconds;
  const e = zdt(slot.end, slot.timeZone).epochMilliseconds;
  const day = slot.start.slice(0, 10);
  const { busy } = busyIntervals(state, addDays(day, -1), addDays(day, 2), key);
  return !busy.some((b) => b.start < e && b.end > s);
}

export class SlotTakenError extends ValidationError {
  readonly reason = 'SLOT_TAKEN';
  constructor() {
    super('Tiden är inte längre ledig. Sök igen.');
    this.name = 'SlotTakenError';
  }
}

/**
 * Skapar förberedelsen som en vanlig egen aktivitet länkad till källposten. Kontrollerar luckan igen
 * (kalendern kan ha ändrats sedan sökningen). Samma opId två gånger (dubbelklick, omförsök) skapar
 * ingenting nytt. Källpostens datum och innehåll rörs aldrig.
 */
export function createPrepWork(
  state: AppState,
  opId: string,
  key: string,
  slot: Pick<PrepSlot, 'start' | 'end' | 'timeZone'>,
  input: { title: string; notes?: string; calendarId?: string },
  now: Temporal.Instant,
): OpResult & { id: string } {
  const src = state.sourceItems[key];
  if (!src) throw new NotFoundError('Den importerade posten');
  if (state.appliedOps[opId]) return createLinkedWork(state, opId, key, { title: input.title, date: slot.start.slice(0, 10) }, now);
  // Förslaget kan ha legat öppet medan tid, källdata eller inställningar ändrats. Skapandet använder
  // aktuell visningszon, så ett förslag från en annan zon måste sökas om i stället för att flyttas tyst.
  if (src.archived || isRecurringSource(state, src) || slot.timeZone !== state.settings.timeZone) {
    throw new ValidationError('Förslaget är inte längre aktuellt. Sök igen.');
  }
  const startMs = zdt(slot.start, slot.timeZone).epochMilliseconds;
  const endMs = zdt(slot.end, slot.timeZone).epochMilliseconds;
  if (startMs < now.epochMilliseconds || endMs <= startMs || endMs > deadlineStart(src, state.settings.timeZone).ms) {
    throw new ValidationError('Förslaget är inte längre aktuellt. Sök igen.');
  }
  if (!isPrepSlotFree(state, key, slot)) throw new SlotTakenError();
  const minutes = Math.round(Temporal.PlainDateTime.from(slot.start).until(Temporal.PlainDateTime.from(slot.end), { largestUnit: 'minutes' }).total({ unit: 'minutes' }));
  return createLinkedWork(
    state,
    opId,
    key,
    { title: input.title, date: slot.start.slice(0, 10), startTime: slot.start.slice(11, 16), durationMinutes: minutes, ...(input.calendarId ? { calendarId: input.calendarId } : {}), ...(input.notes !== undefined ? { notes: input.notes } : {}) },
    now,
  );
}
